import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { joinClassroomByCode } from '../lib/supabaseClassroomAPI';

// If you already have shared UI primitives, import them.
// Otherwise, the plain Tailwind classes below will still look great.
// import Button from '../components/ui/Button';
// import { Card } from '../components/ui/Card';
// import { Input } from '../components/ui/Input';
// import { Spinner } from '../components/ui/Spinner';

export default function JoinClassroom() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const normalizedCode = code.trim().toUpperCase();
  const isValid = /^[A-Z0-9]{6}$/.test(normalizedCode);

  const handleJoin = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!isValid) {
      setErrorMsg('Please enter a valid 6-character code using letters or numbers.');
      return;
    }

    try {
      setLoading(true);
      const classroom = await joinClassroomByCode(normalizedCode, user.id);
      setSuccessMsg(`Joined classroom: ${classroom.name}`);
      setCode('');

      // CTA: go to the classroom immediately
      navigate(`/classrooms/student/${classroom.id}`);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to join classroom.');
    } finally {
      setLoading(false);
    }
  };

  // Split display for nicer code entry (still one input under the hood)
  const displayCells = Array.from({ length: 6 }, (_, i) => normalizedCode[i] || '');

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Join a Classroom</h1>
        <p className="mt-2 text-gray-600">
          Enter your 6-character join code to access your class.
        </p>
      </div>

      {/* Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8">
        <form onSubmit={handleJoin} className="space-y-6">
          {/* Pretty code boxes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Join Code
            </label>

            <div className="flex gap-2 justify-between sm:justify-start">
              {displayCells.map((ch, idx) => (
                <div
                  key={idx}
                  className={`w-12 h-12 sm:w-14 sm:h-14 inline-flex items-center justify-center rounded-lg border text-xl font-semibold tracking-widest
                    ${ch ? 'border-blue-500 text-gray-900 bg-blue-50' : 'border-gray-300 text-gray-400 bg-white'}`}
                >
                  {ch || '•'}
                </div>
              ))}
            </div>

            {/* The real input (screen-reader friendly and keyboard friendly) */}
            <input
              type="text"
              inputMode="latin"
              autoCapitalize="characters"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABC123"
              className="mt-3 w-full sm:w-64 p-3 border border-gray-300 rounded-lg tracking-widest uppercase text-center font-mono outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />

            <p className="mt-2 text-sm text-gray-500">
              Codes are case-insensitive. Use only letters A–Z and digits 0–9.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white px-5 py-2.5 rounded-lg disabled:opacity-50"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4A4 4 0 004 12z"
                    />
                  </svg>
                  Joining…
                </>
              ) : (
                'Join Classroom'
              )}
            </button>

            <button
              type="button"
              onClick={() => setCode('')}
              className="text-gray-700 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg"
            >
              Clear
            </button>
          </div>

          {/* Alerts */}
          {errorMsg && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
              {errorMsg}
            </div>
          )}
          {successMsg && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-green-700">
              {successMsg}
            </div>
          )}

          {/* Tips */}
          <div className="pt-2 text-sm text-gray-500">
            <p>Having trouble? Double-check the code with your teacher.</p>
          </div>
        </form>
      </div>
    </div>
  );
}
